import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { AnimatePresence, LazyMotion, animate, domAnimation, m, useReducedMotion } from 'motion/react';
import '../theme/Colors.stylex';
import { Primitives } from '../theme/Colors.stylex';
export type Status = 'idle' | 'pending' | 'failed';
type Phase = 'running' | 'completing' | 'failing';
type Cycle = {
  id: number;
  shownAt: number;
  remainingDuration: number;
  phase: Phase;
};
export type PredictiveLoadingBarProps = {
  status: Status;
  estimatedDuration?: number;
  appearDelay?: number;
};
export const PROGRESS_START = 8;
export const PROGRESS_CEILING = 95;
export const MIN_REMAINING_DURATION = 300;
export const CURVE_STEEPNESS = 2.5;
export const DEFAULT_ESTIMATED_DURATION = 1000;
export const DEFAULT_APPEAR_DELAY = 150;
export const COMPLETION_DURATION = 250;
export const FAILURE_HOLD_DURATION = 1200;
const FADE_DURATION = 0.2;
const FAILURE_BLINK_DURATION = '450ms';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';
// NOTE - the curve is played over 3.5T, it's then at 8 + 87 × (1 − e^-8.75) ≈ 94.99%
const RUNNING_SPAN = 3.5;
const COMPLETION_EASE = [0.22, 1, 0.36, 1] as const;

// NOTE - starts from a visible step when the bar shows up then eases out towards the
// > ceiling (~90% once the remaining duration elapsed), only the actual end reaches 100%
export const predictedProgress = (elapsed: number, remainingDuration: number) => remainingDuration > 0 ? PROGRESS_START + (PROGRESS_CEILING - PROGRESS_START) * (1 - Math.exp(-CURVE_STEEPNESS * Math.max(elapsed, 0) / remainingDuration)) : PROGRESS_CEILING;

// NOTE - normalized remaining part of the curve, independent from the elapsed time
const runningEase = (progress: number) => (1 - Math.exp(-CURVE_STEEPNESS * RUNNING_SPAN * progress)) / (1 - Math.exp(-CURVE_STEEPNESS * RUNNING_SPAN));
const toTransform = (progress: number) => `translateX(${progress - 100}%)`;

// NOTE - a single blink, the color change alone is hard to notice on a 2px bar
const failureBlink = "x1r8g2zl-B";
type FillProps = {
  cycle: Cycle;
  reducedMotion: boolean;
};

// NOTE - the progress is a single transform animation run by the browser (WAAPI) off the
// > main thread, so it keeps moving while a large rendering blocks the JavaScript.
function PredictiveLoadingBarFill({
  cycle,
  reducedMotion
}: FillProps) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (reducedMotion) {
      element.style.transform = toTransform(100);
      return;
    }
    const {
      shownAt,
      remainingDuration,
      phase
    } = cycle;
    const elapsed = performance.now() - shownAt;
    const from = predictedProgress(elapsed, remainingDuration);
    // NOTE - the running curve is a function of the elapsed time, freezing it there
    // > matches where the stopped animation was
    if (phase === 'failing') {
      element.style.transform = toTransform(from);
      return;
    }
    const controls = phase === 'running' ? animate(element, {
      transform: [toTransform(from), toTransform(predictedProgress(elapsed + RUNNING_SPAN * remainingDuration, remainingDuration))]
    }, {
      duration: RUNNING_SPAN * remainingDuration / 1000,
      ease: runningEase
    }) : animate(element, {
      transform: [toTransform(from), toTransform(100)]
    }, {
      duration: COMPLETION_DURATION * 0.9 / 1000,
      ease: COMPLETION_EASE
    });
    return () => controls.stop();
  }, [cycle, reducedMotion]);
  const failing = cycle.phase === 'failing';
  return <div ref={ref} style={{
    transform: toTransform(0)
  }} {...{
    0: {
      className: "position-x10l6tqk inset-x10a8y8t backgroundImage-x1u8f9fy content-x1s928wv position-x1j6awrg top-x1m1drc7 right-xnbfe2x bottom-x1xrz1ek width-xzb68fv backgroundImage-xr30rdt boxShadow-x8zay9a",
      "data-style-src": "wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:78"
    },
    2: {
      className: "position-x10l6tqk inset-x10a8y8t backgroundImage-x1u8f9fy content-x1s928wv position-x1j6awrg top-x1m1drc7 right-xnbfe2x bottom-x1xrz1ek width-xzb68fv backgroundImage-xr30rdt boxShadow-x8zay9a animationName-x1lfajqe animationName-x1aquc0h animationDuration-x1ykxiw6 animationTimingFunction-x4hg4is display-x1h1zc6f",
      "data-style-src": "wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:78; wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:93"
    },
    1: {
      className: "position-x10l6tqk inset-x10a8y8t backgroundImage-x1u8f9fy content-x1s928wv position-x1j6awrg top-x1m1drc7 right-xnbfe2x bottom-x1xrz1ek width-xzb68fv backgroundImage-xr30rdt boxShadow-x8zay9a opacity-xbyyjgo display-x1h1zc6f",
      "data-style-src": "wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:78; wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:99"
    },
    3: {
      className: "position-x10l6tqk inset-x10a8y8t backgroundImage-x1u8f9fy content-x1s928wv position-x1j6awrg top-x1m1drc7 right-xnbfe2x bottom-x1xrz1ek width-xzb68fv backgroundImage-xr30rdt boxShadow-x8zay9a animationName-x1lfajqe animationName-x1aquc0h animationDuration-x1ykxiw6 animationTimingFunction-x4hg4is opacity-xbyyjgo display-x1h1zc6f",
      "data-style-src": "wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:78; wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:93; wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:99"
    }
  }[!!failing << 1 | !!reducedMotion << 0]}>
      <div {...{
      0: {
        className: "position-x10l6tqk inset-x10a8y8t opacity-xg01cxk backgroundImage-xwzus8y",
        "data-style-src": "wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:103"
      },
      1: {
        className: "position-x10l6tqk inset-x10a8y8t backgroundImage-xwzus8y opacity-x1hc1fzr",
        "data-style-src": "wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:103; wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:109"
      }
    }[!!failing << 0]} />
    </div>;
}
export const PredictiveLoadingBar = memo(function PredictiveLoadingBarComponent({
  status,
  estimatedDuration = DEFAULT_ESTIMATED_DURATION,
  appearDelay = DEFAULT_APPEAR_DELAY
}: PredictiveLoadingBarProps) {
  const reducedMotion = useReducedMotion() ?? false;
  const [cycle, setCycle] = useState<Cycle | null>(null);
  const cycleIdRef = useRef(0);
  // NOTE - stays true while the bar fades out, a new cycle then shows up without delay
  const presentRef = useRef(false);
  useEffect(() => {
    if (status === 'failed') {
      setCycle(current => current?.phase === 'running' ? {
        ...current,
        phase: 'failing'
      } : current);
      return;
    }
    if (status === 'idle') {
      setCycle(current => current?.phase === 'running' ? {
        ...current,
        phase: 'completing'
      } : current);
      return;
    }
    const id = ++cycleIdRef.current;
    const startedAt = performance.now();
    const show = () => {
      presentRef.current = true;
      const shownAt = performance.now();
      // NOTE - the curve starts when the bar shows up and targets the estimated end
      const remainingDuration = Math.max(estimatedDuration - (shownAt - startedAt), MIN_REMAINING_DURATION);
      setCycle({
        id,
        shownAt,
        remainingDuration,
        phase: 'running'
      });
    };
    if (presentRef.current) {
      show();
      return;
    }
    const timer = setTimeout(show, appearDelay);
    return () => clearTimeout(timer);
    // NOTE - the estimate and the delay are frozen when a loading cycle starts
  }, [status]);
  useEffect(() => {
    if (cycle?.phase !== 'completing' && cycle?.phase !== 'failing') return;
    const duration = cycle.phase === 'failing' ? FAILURE_HOLD_DURATION : reducedMotion ? 0 : COMPLETION_DURATION;
    const timer = setTimeout(() => setCycle(current => current?.id === cycle.id ? null : current), duration);
    return () => clearTimeout(timer);
  }, [cycle, reducedMotion]);
  return <LazyMotion features={domAnimation} strict>
      <AnimatePresence onExitComplete={() => presentRef.current = false}>
        {cycle && <m.div key="predictive-loading-bar" aria-hidden data-phase={cycle.phase} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: reducedMotion ? 0 : FADE_DURATION
      }} className="position-x10l6tqk insetInline-x17y0mx6 bottom-x1ey2m1c height-x36qwtl overflow-xb3r6kr pointerEvents-x47corl" data-style-src="wino-gc:src/resources/feedback-indicators/PredictiveLoadingBar.tsx:70">
            <PredictiveLoadingBarFill cycle={cycle} reducedMotion={reducedMotion} />
          </m.div>}
      </AnimatePresence>
    </LazyMotion>;
});